import { useTranslation } from "react-i18next"

import {
  useApolloClient,
  useMutation as useApolloMutation
} from "@apollo/client"

import { callMutation } from "shared/api/callMutation"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ApiResponse } from "shared/types/ApiResponse"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"
import { MutationOptions } from "shared/types/MutationOptions"
import { TeamMember } from "shared/types/__generated__/graphql"

import { ProjectsDocument } from "entities/projects/graphql/queries.generated"
import { useTeamLazyQuery } from "entities/teams/graphql/queries.generated"

import {
  DeleteTeamMemberDocument,
  DeleteTeamMemberMutation,
  DeleteTeamMemberMutationVariables
} from "../graphql/mutations.generated"
import { useTeamMembersLazyQuery } from "../graphql/queries.generated"
import { evictTeamMember } from "../lib/evictTeamMember"
import { readTeamMember } from "../lib/readTeamMember"

export function useDeleteTeamMember() {
  const { cache } = useApolloClient()
  const { t } = useTranslation()
  const [refetchTeam] = useTeamLazyQuery({
    fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
  })
  const [refetchTeamMembers] = useTeamMembersLazyQuery({
    fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
  })

  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.TeamMember.DeleteTeamMember.Start"),
      success: t("Request.TeamMember.DeleteTeamMember.Success"),
      failure: t("Request.TeamMember.DeleteTeamMember.Failure")
    }
  })

  const [apolloMutate, mutationState] = useApolloMutation<
    DeleteTeamMemberMutation,
    DeleteTeamMemberMutationVariables
  >(DeleteTeamMemberDocument)

  const handleDeleteTeamMember = useEvent(
    async (
      {
        teamID,
        teamMemberID
      }: {
        teamID: string
        teamMemberID: string
      },
      options?: MutationOptions
    ): Promise<ApiResponse<TeamMember>> => {
      const teamMember = readTeamMember(teamMemberID, cache)
      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast({
        suppress: options?.suppressToasts
      })

      evictTeamMember(teamMemberID, cache)
      const result = await callMutation(apolloMutate, {
        variables: { teamID, teamMemberID },
        transform: data => data,
        apolloOptions: {
          onCompleted: () => {
            evictTeamMember(teamMemberID, cache)
            raiseSuccessToast()
          },
          onError: () => {
            refetchTeamMembers({ variables: { teamID } })
            refetchTeam({ variables: { id: teamID } })
            raiseErrorToast()
          },
          refetchQueries: [{ query: ProjectsDocument }]
        }
      })

      if (result.data?.teamMember.success) {
        return {
          data: teamMember || null
        }
      }

      return {
        data: null,
        errors: [
          {
            message: t("Request.TeamMember.DeleteTeamMember.Failure")
          }
        ]
      }
    }
  )

  return {
    deleteTeamMember: handleDeleteTeamMember,
    ...mutationState
  }
}
